import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { injectIntl, FormattedMessage } from 'react-intl';
import { toastr } from 'react-redux-toastr';
import moment from 'moment';

import Loader from '../../../components/Common/Loader/Loader';
import CustomPagination from '../../../components/CustomPagination';
import getDeferredBackgroundChecks from './getDeferredBackgroundChecks.graphql';
import validateDeferredBackgroundCheck from './validateDeferredBackgroundCheck.graphql';
import { decode } from '../../../helpers/queryEncryption';
import messages from '../../../locale/messages';

const DeferredBackgroundChecks = ({ intl: { formatMessage } }) => {
  const [currentPage, setCurrentPage] = useState(1);

  const { loading, data, refetch } = useQuery(getDeferredBackgroundChecks, {
    variables: { currentPage, searchList: '' },
    fetchPolicy: 'network-only',
    ssr: false,
  });

  const [runValidate, { loading: validating }] = useMutation(validateDeferredBackgroundCheck);

  const onValidate = async (id) => {
    try {
      const res = await runValidate({ variables: { id } });
      if (res?.data?.validateDeferredBackgroundCheck?.status === 200) {
        toastr.success('Success', formatMessage(messages.deferredValidateSuccess));
        await refetch();
      } else {
        toastr.error('Error', res?.data?.validateDeferredBackgroundCheck?.errorMessage || 'Something went wrong');
      }
    } catch (e) {
      toastr.error('Error', 'Something went wrong');
    }
  };

  const results = data?.getDeferredBackgroundChecks?.results || [];
  const count = data?.getDeferredBackgroundChecks?.count || 0;

  const paginationData = (page) => {
    setCurrentPage(page);
    refetch({ currentPage: page, searchList: '' });
  };

  return (
    <Loader type={'page'} show={loading}>
      <h1 className="siteAdminPrimaryHeading"><FormattedMessage {...messages.deferredBackgroundChecks} /></h1>
      {!loading && (
        results.length === 0 ? (
          <p><FormattedMessage {...messages.deferredNoRequests} /></p>
        ) : (
          <>
          <table className="table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th scope="col"><FormattedMessage {...messages.id} /></th>
                <th scope="col"><FormattedMessage {...messages.firstName} /></th>
                <th scope="col"><FormattedMessage {...messages.email} /></th>
                <th scope="col"><FormattedMessage {...messages.amount} /></th>
                <th scope="col"><FormattedMessage {...messages.paymentStatus} /></th>
                <th scope="col"><FormattedMessage {...messages.deferredRequestedAt} /></th>
                <th scope="col"><FormattedMessage {...messages.action} /></th>
              </tr>
            </thead>
            <tbody>
              {results.map((item) => {
                const isPending = item?.status === 'pending';
                const isPaid = item?.status === 'completed';
                return (
                  <tr key={item?.id}>
                    <td data-label={formatMessage(messages.id)}>{item?.profileId}</td>
                    <td data-label={formatMessage(messages.firstName)}>
                      {decode(item?.encryptFirstName)} {decode(item?.encryptLastName)}
                    </td>
                    <td data-label={formatMessage(messages.email)}>{decode(item?.encryptEmail)}</td>
                    <td data-label={formatMessage(messages.amount)}>{item?.currency} {item?.amount}</td>
                    <td data-label={formatMessage(messages.paymentStatus)}>
                      {isPaid ? (
                        <span style={{ color: '#0E9488', fontWeight: 600 }}>
                          <FormattedMessage {...messages.bgPaymentPaid} />
                        </span>
                      ) : (
                        <FormattedMessage {...messages.bgPaymentDeferred} />
                      )}
                    </td>
                    <td data-label={formatMessage(messages.deferredRequestedAt)}>
                      {item?.createdAt ? moment(Number(item.createdAt)).format('DD-MM-YYYY HH:mm') : '-'}
                    </td>
                    <td data-label={formatMessage(messages.action)}>
                      {isPending ? (
                        <button
                          type="button"
                          className="btn btn-primary"
                          disabled={validating}
                          onClick={() => onValidate(item?.id)}
                        >
                          <FormattedMessage {...messages.deferredValidate} />
                        </button>
                      ) : (
                        <span style={{ color: '#0E9488', fontWeight: 600 }}>
                          <FormattedMessage {...messages.deferredValidated} />
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {count > 10 && (
            <CustomPagination
              total={count}
              currentPage={currentPage}
              defaultCurrent={1}
              defaultPageSize={10}
              change={paginationData}
              paginationLabel="requests"
            />
          )}
          </>
        )
      )}
    </Loader>
  );
};

export default injectIntl(DeferredBackgroundChecks);
